// ============================================================================
// apps/web/src/lib/kiosk.ts
// ----------------------------------------------------------------------------
// Session « kiosque » = compte partagé (kiosk@…) verrouillé sur UN point de
// vente. Une tablette de PV se connecte via le mot de passe DU PV (Worker
// /api/kiosk-pv/auth), ce qui pose `magic.kiosk.slug` en localStorage.
//
// Le kiosque est CONFINÉ à son PV : pas d'accès aux autres PV, pas de
// navigation globale (Réglages, accueil multi-PV, autres modules), Caisse
// verrouillée sur son PV, aucune donnée personnelle du staff exposée. Le seul
// moyen de changer de PV est de ressaisir le mot de passe d'un autre PV.
//
// Helper unique pour éviter de dupliquer email + clé de storage dans chaque
// page (dashboard PV, 4-en-1, caisse, shell…).
// ============================================================================

import {
  Book, BookText, Calendar, Camera, ChefHat, ClipboardList, CookingPot, GraduationCap, Mail, MessageSquare, ShieldCheck, Shirt, Wallet, Zap,
  type LucideIcon,
} from 'lucide-react';
import { useAuth } from './auth-context';
import { supabase } from './supabase';

export const KIOSK_EMAIL = 'kiosk@lepitchoun-traiteur.fr';
export const KIOSK_SLUG_KEY = 'magic.kiosk.slug';

export const MENU_URL = 'https://menu.lepitchoun-traiteur.fr/?admin';
// Menu PUBLIC de consultation (la carte du mois telle que la voient les clients,
// sans connexion). Destine au kiosk cuisine : consultation, pas composition.
export const MENU_PUBLIC_URL = 'https://menu.lepitchoun-traiteur.fr';
export const TODO_URL = 'https://todo.lepitchoun-traiteur.fr';

// ─── Catalogue des modules affichables dans le rail kiosque ─────────────────
// L'admin choisit lesquels apparaissent (Réglages → Kiosque). « Accueil » est
// toujours présent (non listé ici). `to` = route interne (fn du slug PV) ;
// `href` = lien externe (Menu public).
export type KioskModuleDef = {
  key: string;
  label: string;
  icon: LucideIcon;
  to?: (slug: string | null) => string;
  href?: string;
};

export const KIOSK_MODULE_CATALOG: KioskModuleDef[] = [
  // Production (Cuisine) en premier du rail par defaut (ordre du catalogue =
  // ordre d'affichage) : c'est le module central du kiosk cuisine.
  { key: 'production', label: 'Production', icon: CookingPot,    to: () => '/production' },
  { key: 'saisie',     label: 'Saisie',     icon: Zap,           to: (s) => (s ? `/feedback/new?pv=${s}` : '/feedback/new') },
  { key: 'caisse',     label: 'Caisse',     icon: Wallet,        to: () => '/cash' },
  { key: 'planning',   label: 'Planning',   icon: Calendar,      to: () => '/planning' },
  { key: 'feedback',   label: 'Retours',    icon: MessageSquare, to: () => '/feedback' },
  { key: 'menu',       label: 'Menu',       icon: ChefHat,       href: MENU_URL },
  // Menu public (consultation libre, comme les clients) — notamment pour la cuisine.
  { key: 'menu-public', label: 'Menu du mois', icon: ChefHat,    href: MENU_PUBLIC_URL },
  { key: 'hygiene',    label: 'Hygiène',    icon: ShieldCheck,   to: () => '/hygiene' },
  { key: 'scan',       label: 'Traçabilité', icon: Camera,       to: (s) => (s ? `/scan/${s}` : '/scan') },
  { key: 'formation',  label: 'Formation',  icon: GraduationCap, to: () => '/formation' },
  { key: 'newsletter', label: 'Newsletter', icon: Mail,          to: () => '/newsletter' },
  { key: 'wardrobe',   label: 'Tenues',     icon: Shirt,         to: () => '/wardrobe' },
  // Référence/aide, regroupés en bas du rail : Documents + Procédures juste
  // au-dessus du Mode d'emploi (help toujours en dernier).
  { key: 'legal',      label: 'Documents',  icon: ClipboardList, to: () => '/legal' },
  { key: 'procedures', label: 'Procédures', icon: BookText,      to: () => '/procedures' },
  { key: 'help',       label: 'Mode d\'emploi', icon: Book,      to: () => '/help' },
];

// Défaut = doit rester aligné avec le seed de la migration 0097.
export const DEFAULT_KIOSK_MODULES = [
  'saisie', 'caisse', 'hygiene', 'legal', 'procedures', 'formation', 'menu',
];

const SETTINGS_KEY = 'kiosk_modules';

// Config des modules du kiosque : un défaut (tous les kiosques) + des overrides
// par PV (clé = slug). Stockée sous l'UNIQUE clé `kiosk_modules` (la RLS 0097
// n'autorise la lecture publique que de cette clé). Rétro-compatible avec
// l'ancien format (tableau simple = le défaut).
export type KioskModulesConfig = { default: string[]; byLocation: Record<string, string[]> };

let configCache: KioskModulesConfig | null = null;
let inflight: Promise<KioskModulesConfig> | null = null;

function parseConfig(raw: string | null | undefined): KioskModulesConfig {
  try {
    const parsed = raw ? JSON.parse(raw) : null;
    if (Array.isArray(parsed)) return { default: parsed.filter((k): k is string => typeof k === 'string'), byLocation: {} };
    if (parsed && typeof parsed === 'object') {
      const def = Array.isArray(parsed.default) ? parsed.default.filter((k: unknown): k is string => typeof k === 'string') : DEFAULT_KIOSK_MODULES;
      const by: Record<string, string[]> = {};
      if (parsed.byLocation && typeof parsed.byLocation === 'object') {
        for (const [slug, keys] of Object.entries(parsed.byLocation)) {
          if (Array.isArray(keys)) by[slug] = (keys as unknown[]).filter((k): k is string => typeof k === 'string');
        }
      }
      return { default: def, byLocation: by };
    }
  } catch { /* fallthrough */ }
  return { default: DEFAULT_KIOSK_MODULES, byLocation: {} };
}

async function loadConfig(): Promise<KioskModulesConfig> {
  if (configCache) return configCache;
  if (inflight) return inflight;
  inflight = (async () => {
    let cfg: KioskModulesConfig;
    try {
      const { data } = await supabase.from('internal_settings').select('value').eq('key', SETTINGS_KEY).maybeSingle();
      cfg = parseConfig((data as { value: string | null } | null)?.value);
    } catch { cfg = { default: DEFAULT_KIOSK_MODULES, byLocation: {} }; }
    configCache = cfg; inflight = null;
    return cfg;
  })();
  return inflight;
}

/** Clés de modules activés pour le rail kiosque — override du PV `slug` sinon défaut. */
export async function loadKioskModules(slug?: string | null): Promise<string[]> {
  const cfg = await loadConfig();
  return (slug && cfg.byLocation[slug]) ? cfg.byLocation[slug] : cfg.default;
}

/** Config complète (défaut + par PV) pour l'écran Réglages (relecture fraîche). */
export async function loadKioskModulesConfig(): Promise<KioskModulesConfig> {
  configCache = null;
  return loadConfig();
}

/** Enregistre la config complète (admin) et rafraîchit le cache. */
export async function saveKioskModulesConfig(cfg: KioskModulesConfig): Promise<{ error: string | null }> {
  const { error } = await supabase.from('internal_settings')
    .upsert({ key: SETTINGS_KEY, value: JSON.stringify(cfg) }, { onConflict: 'key' });
  if (error) return { error: error.message };
  configCache = cfg;
  return { error: null };
}

/** Slug du PV verrouillé de la session kiosque (localStorage), sinon null. */
export function getKioskSlug(): string | null {
  try {
    return localStorage.getItem(KIOSK_SLUG_KEY);
  } catch {
    return null;
  }
}

/**
 * Session kiosque courante : `isKiosk` (compte partagé) + `slug` du PV
 * verrouillé. `slug` n'est renvoyé que pour une vraie session kiosque (un slug
 * périmé sur l'appareil d'un utilisateur normal est ignoré).
 */
export function useKiosk(): { isKiosk: boolean; slug: string | null } {
  const { me } = useAuth();
  const isKiosk = me?.profile.email === KIOSK_EMAIL;
  return { isKiosk, slug: isKiosk ? getKioskSlug() : null };
}
